import { useEffect, useState } from "react";
import { History as HistoryIcon, LogIn, UploadCloud } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const fmt = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function History() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/seller/history")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const visits = rows.filter((r) => r.type === "visit").length;
  const uploads = rows.filter((r) => r.type === "upload").length;

  return (
    <div className="space-y-5" data-testid="seller-history-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2">
          <HistoryIcon className="h-6 w-6 text-primary" /> History
        </h1>
        <p className="text-muted-foreground text-sm font-medium">A record of your panel visits and product uploads.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Stat label="Total Visits" value={visits} />
        <Stat label="Total Uploads" value={uploads} />
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground font-semibold">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground font-semibold" data-testid="history-empty">No activity recorded yet.</div>
      ) : (
        <div className="relative pl-6">
          <div className="absolute left-2 top-1 bottom-1 w-px bg-border" />
          <div className="space-y-4">
            {rows.map((r) => {
              const upload = r.type === "upload";
              const Icon = upload ? UploadCloud : LogIn;
              return (
                <div key={r.id} className="relative" data-testid={`history-row-${r.id}`}>
                  <span className={`absolute -left-[18px] top-1 h-4 w-4 rounded-full grid place-items-center ${upload ? "bg-emerald-500" : "bg-blue-500"}`}>
                    <Icon className="h-2.5 w-2.5 text-white" />
                  </span>
                  <div className="bg-card border rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={`border font-bold ${upload ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-blue-100 text-blue-700 border-blue-200"}`}>{upload ? "Upload" : "Visit"}</Badge>
                        <span className="font-semibold text-sm truncate">{r.detail}</span>
                      </div>
                      {upload && (r.category || r.city) && (
                        <div className="text-xs text-muted-foreground font-medium mt-1 capitalize">{[r.category, r.city].filter(Boolean).join(" · ")}</div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground font-medium shrink-0">{fmt(r.at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border rounded-xl px-4 py-3 min-w-[130px]">
      <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">{label}</div>
      <div className="font-heading font-extrabold text-xl mt-0.5">{value}</div>
    </div>
  );
}
